import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

function randInt(min, max, exclude = []) {
  let n;
  do {
    n = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(n));
  return n;
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    [x, y] = [y, x % y];
  }
  return x || 1;
}

function simplifyFraction(num, den) {
  const sign = den < 0 ? -1 : 1;
  const g = gcd(num, den);
  return { num: (sign * num) / g, den: Math.abs(den) / g };
}

function explicitMultiply(expr) {
  let s = expr;
  s = s.replace(/(\d)(x)/g, "$1*$2");
  s = s.replace(/(\d)\(/g, "$1*(");
  s = s.replace(/\)(\d|x)/g, ")*$1");
  s = s.replace(/x\(/g, "x*(");
  return s;
}

function normalizeExpression(raw) {
  let s = typeof raw === "string" ? raw : "";
  s = s.replace(/x\^\((-?\d+\/\d+)\)/g, "x^($1)");
  s = s.replace(/x\^(-?\d+\/\d+)/g, "x^($1)");
  s = s.replace(/^\s*f'?\(x\)\s*=\s*/i, "");
  s = s.replace(/^\s*y\s*=\s*/i, "");
  s = s.replace(/\u2212/g, "-");
  s = s.replace(/(\d),(\d)/g, "$1.$2");
  s = s.replace(/\s+/g, "");
  s = explicitMultiply(s);
  s = s.replace(/\^/g, "**");
  return s;
}

function safeEval(expr, x) {
  const normalized = normalizeExpression(expr);
  if (!normalized) return NaN;
  const js = normalized.replace(/x/g, `(${x})`);
  try {
    const value = Function(`"use strict"; return (${js});`)();
    return Number.isFinite(value) ? value : NaN;
  } catch {
    return NaN;
  }
}

function areEquivalent(userExpr, expectedExpr) {
  const points = [1, 2, 4, 9];
  for (const x of points) {
    const userVal = safeEval(userExpr, x);
    const expectedVal = safeEval(expectedExpr, x);
    if (!Number.isFinite(userVal) || !Number.isFinite(expectedVal)) return false;
    if (Math.abs(userVal - expectedVal) > 0.001) return false;
  }
  return true;
}

function fractionLatex(num, den) {
  return den === 1 ? `${num}` : `\\dfrac{${num}}{${den}}`;
}

function exponentLatex(num, den) {
  const fr = simplifyFraction(num, den);
  if (fr.den === 1) return `${fr.num}`;
  if (fr.num < 0) return `-\\tfrac{${Math.abs(fr.num)}}{${fr.den}}`;
  return `\\tfrac{${fr.num}}{${fr.den}}`;
}

function exponentAscii(num, den) {
  const fr = simplifyFraction(num, den);
  if (fr.den === 1) return `${fr.num}`;
  return `${fr.num}/${fr.den}`;
}

function splitTerms(expr) {
  const s = String(expr || "").replace(/\s+/g, "");
  const terms = [];
  let current = "";
  let depth = 0;

  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (ch === "(") depth += 1;
    if (ch === ")") depth = Math.max(0, depth - 1);

    const isSeparator = (ch === "+" || ch === "-") && i > 0 && depth === 0 && s[i - 1] !== "^";
    if (isSeparator) {
      if (current) terms.push(current);
      current = ch;
    } else {
      current += ch;
    }
  }

  if (current) terms.push(current);
  return terms;
}

function formatInputTermLatex(term) {
  let out = term;

  out = out.replace(/^([+-]?\d+)\/(\d+)\*?/, (_m, num, den) => {
    const abs = Math.abs(Number(num));
    const sign = Number(num) < 0 ? "-" : "";
    return `${sign}${fractionLatex(abs, Number(den))}`;
  });

  out = out.replace(/x\^\((-?\d+)\/(\d+)\)/g, (_m, num, den) => `x^{${exponentLatex(Number(num), Number(den))}}`);
  out = out.replace(/x\^(-?\d+\/\d+)/g, (_m, frac) => {
    const [num, den] = frac.split("/").map(Number);
    return `x^{${exponentLatex(num, den)}}`;
  });
  out = out.replace(/x\^(-\d+)/g, "x^{$1}");
  out = out.replace(/\*/g, "\\cdot ");
  return out;
}

function toLatexPreview(raw) {
  return splitTerms(raw)
    .map((term, idx) => {
      const formatted = formatInputTermLatex(term);
      if (idx === 0) return formatted;
      if (formatted.startsWith("-")) return ` - ${formatted.slice(1)}`;
      return ` + ${formatted.replace(/^\+/, "")}`;
    })
    .join("");
}

function polynomialTermLatex(coef, power, isFirst = false) {
  const sign = coef < 0 ? "-" : "+";
  const abs = Math.abs(coef);
  const prefix = isFirst ? (coef < 0 ? "-" : "") : ` ${sign} `;

  if (power === 0) return `${prefix}${abs}`;
  if (power === 1) return `${prefix}${abs === 1 ? "x" : `${abs}x`}`;
  return `${prefix}${abs === 1 ? `x^{${power}}` : `${abs}x^{${power}}`}`;
}

function polynomialTermAsciiCompact(coef, power) {
  const abs = Math.abs(coef);
  const sign = coef < 0 ? "-" : "";

  if (power === 0) return `${sign}${abs}`;
  if (power === 1) return `${sign}${abs === 1 ? "x" : `${abs}x`}`;
  return `${sign}${abs === 1 ? `x^${power}` : `${abs}x^${power}`}`;
}

function radicalOriginalLatex(term, isFirst = false) {
  const sign = term.coef < 0 ? "-" : "+";
  const abs = Math.abs(term.coef);
  const prefix = isFirst ? (term.coef < 0 ? "-" : "") : ` ${sign} `;
  const rootBody = term.den === 2
    ? (term.num === 1 ? "\\sqrt{x}" : `\\sqrt{x^{${term.num}}}`)
    : (term.num === 1 ? "\\sqrt[3]{x}" : `\\sqrt[3]{x^{${term.num}}}`);

  if (abs === 1) return `${prefix}${rootBody}`;
  return `${prefix}${abs}${rootBody}`;
}

function radicalPowerLatex(term, isFirst = false) {
  const sign = term.coef < 0 ? "-" : "+";
  const abs = Math.abs(term.coef);
  const prefix = isFirst ? (term.coef < 0 ? "-" : "") : ` ${sign} `;
  const exp = exponentLatex(term.num, term.den);
  if (abs === 1) return `${prefix}x^{${exp}}`;
  return `${prefix}${abs}x^{${exp}}`;
}

function radicalDerivativeLatex(term, isFirst = false) {
  const derived = simplifyFraction(term.coef * term.num, term.den);
  const expNum = term.num - term.den;
  const expDen = term.den;
  const sign = derived.num < 0 ? "-" : "+";
  const absNum = Math.abs(derived.num);
  const prefix = isFirst ? (derived.num < 0 ? "-" : "") : ` ${sign} `;
  const exp = exponentLatex(expNum, expDen);

  if (absNum === 1 && derived.den === 1) return `${prefix}x^{${exp}}`;
  if (derived.den === 1) return `${prefix}${absNum}x^{${exp}}`;
  return `${prefix}${fractionLatex(absNum, derived.den)}x^{${exp}}`;
}

function radicalDerivativeAscii(term) {
  const coef = simplifyFraction(term.coef * term.num, term.den);
  const expNum = term.num - term.den;
  const expDen = term.den;
  const exp = exponentAscii(expNum, expDen);

  let coefPart = "";
  if (coef.den === 1) {
    if (coef.num === 1) coefPart = "";
    else if (coef.num === -1) coefPart = "-";
    else coefPart = `${coef.num}*`;
  } else {
    coefPart = `${coef.num}/${coef.den}*`;
  }

  return `${coefPart}x^(${exp})`;
}

function joinAsciiTerms(terms) {
  return terms
    .filter(Boolean)
    .map((term, idx) => {
      if (idx === 0) return term;
      return term.startsWith("-") ? ` - ${term.slice(1)}` : ` + ${term}`;
    })
    .join("");
}

function buildTaskData() {
  const polyCount = randInt(1, 2);
  const usedPowers = new Set();
  const polynomialTerms = [];
  while (polynomialTerms.length < polyCount) {
    const power = randInt(0, 3);
    if (usedPowers.has(power)) continue;
    usedPowers.add(power);
    polynomialTerms.push({ coef: randInt(-6, 6, [0]), power });
  }

  const radicalTemplates = [
    { num: 1, den: 2 },
    { num: 1, den: 3 },
    { num: 2, den: 3 },
  ];
  const radical = {
    ...radicalTemplates[randInt(0, radicalTemplates.length - 1)],
    coef: randInt(-6, 6, [0]),
  };

  polynomialTerms.sort((a, b) => b.power - a.power);
  return { polynomialTerms, radical };
}

export function generateTask() {
  const { polynomialTerms, radical } = buildTaskData();
  const derivativePolynomials = polynomialTerms.filter((t) => t.power !== 0);

  const functionLatex = [
    ...polynomialTerms.map((t, idx) => polynomialTermLatex(t.coef, t.power, idx === 0)),
    radicalOriginalLatex(radical, polynomialTerms.length === 0),
  ].join("");

  const derivativeLatex = [
    ...derivativePolynomials.map((t, idx) => polynomialTermLatex(t.coef * t.power, t.power - 1, idx === 0)),
    radicalDerivativeLatex(radical, derivativePolynomials.length === 0),
  ].join("");

  const derivativeAscii = joinAsciiTerms([
    ...derivativePolynomials.map((t) => polynomialTermAsciiCompact(t.coef * t.power, t.power - 1)),
    radicalDerivativeAscii(radical),
  ]);

  return {
    type: "RadicalDerivativeTask",
    prompt: `Oblicz pochodna funkcji: f(x) = ${functionLatex}. Podaj f'(x) w zapisie potegowym.`,
    answer: derivativeAscii,
    data: {
      polynomialTerms,
      radical,
      functionLatex,
      derivativeLatex,
      derivativeAscii,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>Oblicz pochodna funkcji:</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <p>
        Podaj <InlineMath math={"f'(x)"} /> w zapisie potegowym,
        np. <InlineMath math={"x^{1/2}"} /> albo <InlineMath math={"x^{-1/2}"} />.
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const hasPreview = typeof userAnswer === "string" && userAnswer.trim() !== "";

  return (
    <div className="flex flex-col gap-2">
      <div><InlineMath math={"f'(x) ="} /></div>
      <EquationInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        placeholder="np. 6x+1/2*x^(-1/2)"
        basis="clamp(220px, 34ch, 340px)"
        showPreview={false}
      />
      {hasPreview ? (
        <div>
          <strong>Podgląd:</strong>{" "}
          <InlineMath math={`f'(x) = ${toLatexPreview(userAnswer)}`} />
        </div>
      ) : null}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (typeof userAnswer !== "string" || !userAnswer.trim()) return false;
  return areEquivalent(userAnswer, data.derivativeAscii);
}

export function renderUserAnswer(userAnswer) {
  if (typeof userAnswer !== "string" || !userAnswer.trim()) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={`f'(x) = ${toLatexPreview(userAnswer)}`} />;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`f'(x) = ${data.derivativeLatex}`} />;
}

export function renderExplanation({ data }) {
  const positiveSteps = data.polynomialTerms.map((t) => {
    const original = polynomialTermLatex(t.coef, t.power, true).replace(/^\+\s*/, "");
    if (t.power === 0) {
      return { original, derived: "0" };
    }
    return {
      original,
      derived: polynomialTermLatex(t.coef * t.power, t.power - 1, true).replace(/^\+\s*/, ""),
    };
  });

  const radicalOriginal = radicalOriginalLatex(data.radical, true).replace(/^\+\s*/, "");
  const radicalRewritten = radicalPowerLatex(data.radical, true).replace(/^\+\s*/, "");
  const radicalDerived = radicalDerivativeLatex(data.radical, true).replace(/^\+\s*/, "");

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1:</strong> Zamieniamy pierwiastek na potegę wymierną.
      </p>
      <BlockMath math={`${radicalOriginal} = ${radicalRewritten}`} />

      <p>
        <strong>Krok 2:</strong> Różniczkujemy skladnik z potegą wymierną regułą <InlineMath math={"(ax^r)' = a \\cdot r x^{r-1}"} />.
      </p>
      <BlockMath math={`${radicalRewritten} \\rightarrow ${radicalDerived}`} />

      {positiveSteps.length > 0 && (
        <>
          <p><strong>Krok 3:</strong> Różniczkujemy pozostale skladniki:</p>
          {positiveSteps.map((step, idx) => (
            <BlockMath key={idx} math={`${step.original} \\rightarrow ${step.derived}`} />
          ))}
        </>
      )}

      <p><strong>Krok 4:</strong> Zapisujemy wynik:</p>
      <BlockMath math={`f'(x) = ${data.derivativeLatex}`} />
    </div>
  );
}


